<template>
	<div class="session-control-webrtc">
		<div class="session-control-webrtc__media">
			<video
				autoplay
				muted
				playsinline
			></video>
			<div v-if="activeSession" class="session-control-webrtc__media__actions_overlay">
				<ui-button
					:icon="`fal fa-fw ${audioMuted ? 'fa-microphone-slash' : 'fa-microphone'}`"
					:tooltip="`${audioMuted ? 'Unmute audio' : 'Mute audio'}`"
					class="session-control-webrtc__actions_overlay_button"
					@click="toggleAudioMute"
				/>
				<ui-button
					:icon="`fal fa-fw ${videoMuted ? 'fa-video-slash': 'fa-video'}`"
					:tooltip="`${videoMuted ? 'Unmute video' : 'Mute video'}`"
					class="session-control-webrtc__actions_overlay_button"
					@click="toggleVideoMute"
				/>
				<ui-button
					class="session-control-webrtc__actions_overlay_button"
					:tooltip="`${displayMediaStream ? 'Stop sharing screen' : 'Share screen'}`"
					@click="toggleScreenSharing"
				>
					<!-- eslint-disable-next-line vue/v-slot-style -->
					<template v-slot:icon>
						<span
							v-if="displayMediaStream"
							class="fa-stack"
							style="font-size:18px;top:2px"
						>
							<i class="fal fa-desktop fa-stack-1x fa-lg"></i>
							<i class="fal fa-times fa-stack-1x fa-sm" style="top:-3px"></i>
						</span>
						<i v-else class="fal fa-desktop fa-lg" style="font-size:24px;margin-top:5px"></i>
					</template>
				</ui-button>
			</div>
		</div>
		<div
			v-if="!activeSession"
			class="session-control-webrtc__controls"
		>
			<ui-button
				:disabled="!stream || isConnecting"
				:icon="`fas ${isConnecting ? 'fa-circle-notch fa-spin' : 'fa-circle'}`"
				:text="streamId !== null ? 'STREAM' : 'RECORD'"
				large
				secondary
				raised
				@click="startSession"
			/>
			<ui-select
				v-model="selectedBandwidth"
				label="Bandwidth"
			>
				<ui-select-item
					v-for="bandwidth in bandwidths"
					:key="bandwidth.value"
					:label="bandwidth.label"
					:value="bandwidth.value"
					@click.native="setDefaultBandwidth(bandwidth.value)"
				/>
			</ui-select>
			<div>
				<ui-select
					v-model="selectedAudioInput"
					:disabled="audioInputs.length === 0"
					label="Audio source"
				>
					<!-- using @click.native on items instead because @select on parent gets triggered programatically -->
					<ui-select-item
						v-for="audioInput in audioInputs"
						:key="audioInput.deviceId"
						:label="removeIdFromDeviceLabel(audioInput.label)"
						:value="audioInput.deviceId"
						@click.native="setDefaultAudioInput(audioInput.deviceId)"
					/>
				</ui-select>
				<vu-meter
					v-if="stream && audioInputs.length > 0"
					:stream="stream"
				/>
			</div>
			<ui-select
				v-model="selectedVideoInput"
				:disabled="videoInputs.length === 0"
				label="Video source"
			>
				<!-- using @click.native on items instead because @select on parent gets triggered programatically -->
				<ui-select-item
					v-for="videoInput in videoInputs"
					:key="videoInput.deviceId"
					:label="removeIdFromDeviceLabel(videoInput.label)"
					:value="videoInput.deviceId"
					@click.native="setDefaultVideoInput(videoInput.deviceId)"
				/>
			</ui-select>
		</div>
		<stream-statistics
			v-if="activeSession"
			:session="activeSession"
			:stream-title="streamTitle"
			@stop="stopSession"
		/>
	</div>
</template>

<script>
import StreamStatistics from "session/item/StreamStatistics";
import VuMeter from "session/ui/VuMeter";
import UiButton from "lib/components/ui/Button";
import UiSelect from "lib/components/ui/Select";
import UiSelectItem from "lib/components/ui/SelectItem";
import alert from "lib/modules/Alert";
import WebRtc from "lib/modules/WebRtc";

import { getRandomString } from "lib/modules/Util";

export default {
	name: "SessionControlWebRtc",
	components: {
		StreamStatistics,
		VuMeter,
		UiButton,
		UiSelect,
		UiSelectItem
	},
	props: {
		recorder: {
			type: Object,
			required: true
		},
		sessionList: {
			type: Array,
			required: true
		},
		streamList: {
			type: Array,
			required: true
		},
		streamId: {
			required: true,
			validator: (prop) => typeof prop === "string" || prop === null // custom validator since null is not a valid type
		}
	},
	data() {
		return {
			activeSession: null,
			audioInputs: [],
			audioMuted: false,
			bandwidths: [{
				label: "Low (256 kbps)",
				value: 256
			}, {
				label: "Medium (768 kbps)",
				value: 768
			}, {
				label: "High (2048 kbps)",
				value: 2048
			}, {
				label: "Maximum (4096 kbps)",
				value: 4096
			}],
			displayMediaStream: null,
			isConnecting: false,
			isScreenSharingPending: false,
			videoInputs: [],
			videoMuted: false,
			selectedAudioInput: null,
			selectedBandwidth: null,
			selectedVideoInput: null,
			stream: null,
			webRtc: null
		};
	},
	computed: {
		constraints() {
			return {
				...this.selectedAudioInput !== null && {
					audio: {
						deviceId: { exact: this.selectedAudioInput }
					}
				},
				...this.selectedVideoInput !== null && {
					video: {
						aspectRatio: 1.777777778,
						deviceId: { exact: this.selectedVideoInput },
						height: { min: 360, ideal: 720, max: 1080 },
						width: { min: 640, ideal: 1280, max: 1920 }
					}
				}
			};
		},
		streamTitle() {
			const currentStream = this.streamList.filter((stream) => stream.stream_id === this.streamId);

			return currentStream.length > 0 ? currentStream[0].title : "Recording";
		}
	},
	watch: {
		sessionList(sessions) {
			this.activeSession = sessions.filter((session) => session.uri && session.uri === this.webRtcUri)[0] || null;

			if (this.activeSession) {
				this.activeSession._hidden = true;
				this.isConnecting = false;
			}
		}
	},
	mounted() {
		this.selectedBandwidth = localStorage.getItem("user.webrtc.bandwidth") || this.bandwidths[2].value;

		this.webRtc = new WebRtc();

		// getUserMedia() needs to be called initially to prompt user for access before calling getDevices()
		this.webRtc.start()
		.then(() => {
			this.getDevices().then(() => {
				// set up listener for changes to media devices
				navigator.mediaDevices.ondevicechange = () => this.getDevices().catch((error) => alert.error(error.message));
			});
		})
		.catch((error) => alert.error(error.message));
	},
	beforeDestroy() {
		if (this.displayMediaStream)
			this.displayMediaStream.getTracks().forEach((track) => track.stop());

		this.webRtc.destroy();
	},
	methods: {
		getDevices() {
			return WebRtc.getDevices().then((devices) => {
				this.audioInputs = devices.audioInputs;
				this.videoInputs = devices.videoInputs;
				this.getDefaultDevices();
				this.startLocalStream();
			})
			.catch((error) => alert.error(error.message));
		},
		getDefaultDevices() {
			const defaultAudioInputDeviceId = localStorage.getItem("user.webrtc.audio_id");
			const defaultVideoInputDeviceId = localStorage.getItem("user.webrtc.video_id");
			const defaultAudioInput = this.audioInputs.find((item) => item.deviceId === defaultAudioInputDeviceId);
			const defaultVideoInput = this.videoInputs.find((item) => item.deviceId === defaultVideoInputDeviceId);

			localStorage.removeItem("user.webrtc.audio_id");
			localStorage.removeItem("user.webrtc.video_id");

			if (defaultAudioInput) {
				this.selectedAudioInput = defaultAudioInput.deviceId;
				localStorage.setItem("user.webrtc.audio_id", defaultAudioInputDeviceId);
			}
			else if (this.audioInputs.length > 0)
				this.selectedAudioInput = this.audioInputs[0].deviceId;
			else
				this.selectedAudioInput = null;

			if (defaultVideoInput) {
				this.selectedVideoInput = defaultVideoInput.deviceId;
				localStorage.setItem("user.webrtc.video_id", defaultVideoInputDeviceId);
			}
			else if (this.videoInputs.length > 0)
				this.selectedVideoInput = this.videoInputs[0].deviceId;
			else
				this.selectedVideoInput = null;
		},
		removeIdFromDeviceLabel(label) {
			return label.replace(/\([^()]*\)/g, "").trim();
		},
		setDefaultAudioInput(val) {
			localStorage.setItem("user.webrtc.audio_id", val);
			this.startLocalStream();
		},
		setDefaultBandwidth(val) {
			localStorage.setItem("user.webrtc.bandwidth", val);
		},
		setDefaultVideoInput(val) {
			localStorage.setItem("user.webrtc.video_id", val);
			this.startLocalStream();
		},
		startLocalStream() {
			if (this.webRtc.webSocket)
				return;

			this.webRtc.start(this.constraints)
			.then((stream) => {
				this.stream = stream;

				this.$el.querySelector("video").srcObject = stream;
			})
			.catch((error) => alert.error(error.message));
		},
		startSession() {
			// check if stream is already in use
			if (this.streamId !== null && this.sessionList.find((item) => item.stream_id === this.streamId)) {
				alert.error("Stream is currently in use by another session");

				return;
			}

			const selectedVideoInputObj = this.videoInputs.find((item) => item.deviceId === this.selectedVideoInput);

			this.webRtcUri = `${getRandomString(6).toLowerCase()}@web.rec`;

			this.isConnecting = true;

			this.webRtc.connect({
				bandwidth: this.selectedBandwidth,
				fromName: selectedVideoInputObj ? this.removeIdFromDeviceLabel(selectedVideoInputObj.label) : "WebRTC",
				fromUri: `sip:${this.webRtcUri}`,
				iceServers: [{ urls: ["stun:turn.mnsbone.net"] }],
				uri: `sip:${this.recorder.uri}`
			})
			.catch((error) => {
				alert.error(error.message);
				this.isConnecting = false;
			});

			if (!this.webRtc.webSocket)
				return;

			this.webRtc.webSocket.addEventListener("close", () => {
				this.audioMuted = this.webRtc.muteAudioTrack(false);
				this.videoMuted = this.webRtc.muteVideoTrack(false);
				this.activeSession = null;
				this.isConnecting = false;
				this.stopScreenSharing();
			});
		},
		stopSession() {
			this.webRtc.close();
		},
		toggleAudioMute() {
			this.audioMuted = this.webRtc.muteAudioTrack(!this.audioMuted);
		},
		toggleVideoMute() {
			this.videoMuted = this.webRtc.muteVideoTrack(!this.videoMuted);
		},
		toggleScreenSharing() {
			if (this.isScreenSharingPending || this.stopScreenSharing())
				return;

			this.isScreenSharingPending = true;

			WebRtc.getDisplayMedia()
			.then((stream) => {
				this.displayMediaStream = stream;
				this.webRtc.replaceTracks(stream);
				this.$el.querySelector("video").srcObject = stream;

				const tracks = stream.getTracks();

				if (tracks.length === 0)
					return;

				tracks[0].onended = () => {
					this.webRtc.replaceTracks(this.stream);
					this.$el.querySelector("video").srcObject = this.stream;
					this.displayMediaStream = null;
				};
			})
			.catch((error) => {
				// don't show error if user selects cancel on screen sharing dialog
				if (error.name !== "NotAllowedError")
					alert.error(error.message);
			})
			.finally(() => {
				this.isScreenSharingPending = false;
			});
		},
		stopScreenSharing() {
			if (this.displayMediaStream === null)
				return false;

			this.displayMediaStream.getTracks().forEach((track) => {
				// see https://stackoverflow.com/a/55960232
				track.stop();
				track.dispatchEvent(new Event("ended"));
			});

			return true;
		}
	}
};
</script>

<style lang="scss" scoped>
.session-control-webrtc {
	display: grid;
	grid-auto-flow: row;
	grid-gap: 0;
	grid-template-columns: none;
	grid-template-rows: auto;
	margin-top: 12px;
	.session-control-webrtc__media {
		position: relative;
		.session-control-webrtc__media__actions_overlay {
			position: absolute;
			top: 8px;
			right: 2px;
			/deep/ button {
				background-color: rgba(0, 0, 0, 0.75);
				i {
					color: rgba(255, 255, 255, 0.8);
				}
			}
		}
		video {
			background-color: black;
			grid-row: auto;
			height: auto;
			width: 100%;
		}
	}
	.session-control-webrtc__controls {
		button {
			margin: 0 0 8px 0;
			width: 100%;
		}
		& > div {
			align-self: start;
		}
	}
}

// 2 columns
@media (min-width: 768px) {
	.session-control-webrtc {
		grid-auto-flow: column;
		grid-gap: 0;
		grid-template-columns: auto 288px;
		grid-template-rows: none;
		.session-control-webrtc__media {
			video {
				align-self: stretch;
				justify-self: stretch;
				grid-row: span 4;
			}
		}
		.session-control-webrtc__controls {
			margin-left: 16px;
		}
	}
}
</style>
